import { useCallback, useEffect, useState, type FormEvent } from "react";
import { AppShell } from "../components/AppShell";
import { Icon } from "../components/Icon";
import {
  deleteInvestmentAsset,
  fetchCatalogSnapshot,
  saveInvestmentAsset,
} from "../lib/catalogApi";
import type { InvestmentAsset, InvestmentAssetClass } from "../lib/catalogTypes";
import { SUPPORTED_CURRENCY_CODES } from "../lib/supportedCurrencies";

type Draft = Partial<InvestmentAsset>;

/** Quote sources the app knows how to read, keyed by asset class. */
const PROVIDERS: Record<InvestmentAssetClass, Array<{ id: string; label: string }>> = {
  crypto: [{ id: "coingecko", label: "CoinGecko (falls back to Coinbase)" }],
  stock: [
    { id: "pse", label: "PSE (Philippine Stock Exchange)" },
    { id: "yahoo", label: "Yahoo Finance" },
    { id: "stooq", label: "Stooq (legacy)" },
  ],
};

function blankDraft(): Draft {
  return {
    assetId: "",
    name: "",
    symbol: "",
    assetClass: "crypto",
    exchangeMIC: null,
    quoteCurrencyCode: "USD",
    providerId: "coingecko",
    sortOrder: 0,
    isActive: true,
  };
}

export function InvestmentAssetsPage() {
  const [rows, setRows] = useState<InvestmentAsset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setRows((await fetchCatalogSnapshot()).investmentAssets ?? []);
  }, []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [reload]);

  const assetClass: InvestmentAssetClass = editing?.assetClass === "stock" ? "stock" : "crypto";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing?.assetId || !editing.name || !editing.symbol) return;
    setBusy(true);
    setError(null);
    try {
      await saveInvestmentAsset({
        recordName: editing.recordName,
        assetId: editing.assetId,
        name: editing.name,
        symbol: editing.symbol,
        assetClass,
        exchangeMIC: assetClass === "stock" ? editing.exchangeMIC ?? null : null,
        quoteCurrencyCode: editing.quoteCurrencyCode ?? "USD",
        providerId: editing.providerId ?? null,
        sortOrder: Number(editing.sortOrder ?? 0),
        isActive: editing.isActive !== false,
      });
      setEditing(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  function onChangeClass(next: InvestmentAssetClass) {
    if (!editing) return;
    setEditing({
      ...editing,
      assetClass: next,
      exchangeMIC: next === "crypto" ? null : editing.exchangeMIC ?? "XPHS",
      quoteCurrencyCode:
        next === "crypto" ? "USD" : editing.quoteCurrencyCode === "USD" ? "PHP" : editing.quoteCurrencyCode,
      providerId: PROVIDERS[next][0]?.id ?? null,
    });
  }

  return (
    <AppShell>
      <main className="page">
        <div className="page-header">
          <div>
            <span className="overline">Catalog</span>
            <h1>Investment assets</h1>
            <p>
              Tradable reference data — ticker, venue, quote currency, and quote provider. Holdings,
              quantities, and valuations never leave the user’s private iCloud database.
            </p>
          </div>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => setEditing(blankDraft())}
          >
            <Icon name="plus" size={15} />
            Add asset
          </button>
        </div>
        {error && <div className="banner error">{error}</div>}
        {editing && (
          <form className="panel form-grid" onSubmit={(e) => void onSubmit(e)}>
            <label>
              Asset ID
              <input
                value={editing.assetId ?? ""}
                onChange={(e) => setEditing({ ...editing, assetId: e.target.value })}
                required
                disabled={Boolean(editing.recordName)}
              />
              <span className="field-hint">Stable slug, e.g. btc, aapl, bdo-pse.</span>
            </label>
            <label>
              Name
              <input
                value={editing.name ?? ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                required
              />
            </label>
            <label>
              Symbol
              <input
                value={editing.symbol ?? ""}
                onChange={(e) => setEditing({ ...editing, symbol: e.target.value.toUpperCase() })}
                required
              />
            </label>
            <label>
              Class
              <select
                value={assetClass}
                onChange={(e) => onChangeClass(e.target.value as InvestmentAssetClass)}
              >
                <option value="crypto">crypto</option>
                <option value="stock">stock</option>
              </select>
            </label>
            {assetClass === "stock" && (
              <label>
                Exchange MIC
                <input
                  value={editing.exchangeMIC ?? ""}
                  onChange={(e) =>
                    setEditing({ ...editing, exchangeMIC: e.target.value.toUpperCase() || null })
                  }
                  required
                />
                <span className="field-hint">ISO 10383, e.g. XPHS (PSE), XNAS (Nasdaq).</span>
              </label>
            )}
            <label>
              Quote currency
              <select
                value={editing.quoteCurrencyCode ?? "USD"}
                onChange={(e) => setEditing({ ...editing, quoteCurrencyCode: e.target.value })}
              >
                {SUPPORTED_CURRENCY_CODES.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Quote provider
              <select
                value={editing.providerId ?? ""}
                onChange={(e) => setEditing({ ...editing, providerId: e.target.value || null })}
              >
                <option value="">—</option>
                {PROVIDERS[assetClass].map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Sort
              <input
                type="number"
                value={editing.sortOrder ?? 0}
                onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
              />
            </label>
            <label>
              Active
              <select
                value={editing.isActive === false ? "no" : "yes"}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.value === "yes" })}
              >
                <option value="yes">Active</option>
                <option value="no">Hidden</option>
              </select>
            </label>
            <div className="row-actions full">
              <button className="btn btn-primary" type="submit" disabled={busy}>
                Save
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </form>
        )}
        <div className="panel table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Symbol</th>
                <th>Asset ID</th>
                <th>Class</th>
                <th>Exchange</th>
                <th>Quote</th>
                <th>Provider</th>
                <th>Sort</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.recordName}>
                  <td>{row.name}</td>
                  <td>
                    <code>{row.symbol}</code>
                  </td>
                  <td>
                    <code>{row.assetId}</code>
                  </td>
                  <td>
                    <code>{row.assetClass}</code>
                  </td>
                  <td>{row.exchangeMIC ?? <span className="muted">—</span>}</td>
                  <td>
                    <code>{row.quoteCurrencyCode}</code>
                  </td>
                  <td>{row.providerId ?? <span className="muted">—</span>}</td>
                  <td>{row.sortOrder}</td>
                  <td className="row-actions">
                    <button className="btn btn-secondary" type="button" onClick={() => setEditing(row)}>
                      Edit
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() =>
                        void deleteInvestmentAsset(row.recordName)
                          .then(reload)
                          .catch((err) =>
                            setError(err instanceof Error ? err.message : "Delete failed"),
                          )
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </AppShell>
  );
}
